import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createCheckoutV2, getUserData } from '@utils/api';
import { useAuth } from '@contexts/User.context';
import { useToast } from '@contexts/Toast.context';
import View from '@components/layout/views/View';
import Loader from '@components/loading/Loader';
import Alert from '@components/alert/Alert';
import { isSubscriptionActivated } from '@utils/subscriptionState';

const POLL_INTERVAL_MS = 2000;
// After ACTIVATION_TIMEOUT_MS: show the slow-activation note and poll less often
const SLOW_POLL_INTERVAL_MS = 15000;
const ACTIVATION_TIMEOUT_MS = 60000;
// Total time from landing on the page until polling stops for good
const POLL_STOP_MS = 10 * 60 * 1000;
const REDIRECT_DELAY_MS = 5000;
const VERIFY_EMAIL_COPY =
	'Verify your email address before buying a plan. Use the link we sent you, then try again.';

/**
 * Checkout page (`/checkout`). What it does depends on the query string:
 * - `price_id`: starts a Stripe checkout session through the v2 billing API and
 *   hands over to the Stripe-hosted page. On failure it shows a persistent error
 *   alert plus a toast. For `account_not_verified` both carry the verify-email copy;
 *   otherwise the toast shows the backend envelope message when there is one, and a
 *   generic "try again" line for transport errors.
 * - `success` + `mode=subscription` (back from Stripe): polls the v2 profile every 2 s
 *   until `isSubscriptionActivated`, then redirects. Stripe has already confirmed the
 *   payment, so `access_status: 'pending'` (Stripe's `incomplete`, which the first
 *   webhook often carries) is shown as "activating" too. After 60 s it shows a
 *   slow-activation note with `reference_id` and polls every 15 s, stopping 10 min
 *   after landing on the page. The poll has its own query key, per user; each successful
 *   poll is copied into `['user-me']` so the banner and nav update, except that a poll
 *   which isn't activated never replaces an activated `['user-me']`. A failed poll
 *   leaves `['user-me']` alone (an errored `['user-me']` would log the user out).
 *   An activation seen by `['user-me']` itself (e.g. a window-focus refetch after
 *   polling has stopped) counts too. Once activated the page stays activated, stops
 *   polling and removes the poll from the query cache; it is removed on unmount too.
 * - `success` with any other mode: a one-time payment; confirms it and redirects.
 * - `canceled`: says nothing was charged, then redirects.
 * - none of these: "No plan selected" with a link to `/subscription`.
 *
 * Redirects go to `return_path || '/subscription'`. Premium, organization and admin
 * users (`subscriptionView.view === 'hidden'`) are sent to `/subscription` straight away.
 *
 * Display only: payment gating is the backend's job (HTTP 402, issue #603). The
 * backend also enforces email verification.
 */
export default function Checkout() {
	const { subscriptionView } = useAuth();

	if (subscriptionView?.view === 'hidden') return <Navigate to="/subscription" replace />;

	return <CheckoutFlow />;
}

/** Which of the checkout flows the query string asks for. */
const getFlow = ({ success, canceled, mode, priceId }) => {
	if (success) return mode === 'subscription' ? 'activation' : 'payment_done';
	if (canceled) return 'canceled';
	if (priceId) return 'start';
	return 'no_plan';
};

const Working = ({ text }) => (
	<>
		<h2>{text}</h2>
		<Loader />
	</>
);

function CheckoutFlow() {
	const { currentUser } = useAuth();
	const { addToast } = useToast();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const priceId = searchParams.get('price_id');
	const mode = searchParams.get('mode');
	const returnPath = searchParams.get('return_path');
	const referenceId = searchParams.get('reference_id');
	const flow = getFlow({
		success: searchParams.get('success'),
		canceled: searchParams.get('canceled'),
		mode,
		priceId,
	});
	// `||`, not `??`: an empty return_path must also fall back
	const redirectTo = returnPath || '/subscription';

	// Start: create the Stripe checkout session and hand over to Stripe.
	// The failure is kept in local state, not read from useMutation: with the one-shot
	// guard below, StrictMode's simulated remount detaches the mutation observer from
	// the only mutation, so its isError/error would never update.
	const [startError, setStartError] = useState(null);
	const startFailed = !!startError;
	const { mutate: startCheckout } = useMutation({
		mutationFn: () =>
			createCheckoutV2({
				price_id: priceId,
				mode: mode || undefined,
				return_path: returnPath || '',
			}),
		onSuccess: data => {
			window.location = data.checkout_url;
		},
		onError: err => {
			console.error('Failed initiating checkout process', err);
			setStartError(err);
			const notVerified = err?.code === 'account_not_verified';
			addToast({
				id: notVerified ? 'checkout-account-not-verified' : 'checkout-error',
				bg: 'danger',
				title: "Couldn't start checkout",
				// Never show transport strings ('Max retries exceeded', 'Failed to fetch', …)
				message: notVerified
					? VERIFY_EMAIL_COPY
					: err?.fromBackend
						? err.message
						: 'Please try again in a moment.',
			});
		},
	});

	// One-shot guard: StrictMode runs mount effects twice in dev, which would
	// otherwise create two Stripe sessions.
	const startedRef = useRef(false);
	useEffect(() => {
		if (flow !== 'start' || startedRef.current) return;
		startedRef.current = true;
		startCheckout();
	}, [flow, startCheckout]);

	// Activation: poll the profile on its own key, not on the user context's
	// ['user-me']. A failed refetch puts a query into error, and an errored ['user-me']
	// turns the user into the logged-out default user. Successful polls are copied into
	// ['user-me'] so the whole app picks up the new subscription_state.
	const queryClient = useQueryClient();
	const [timedOut, setTimedOut] = useState(false);
	const [pollStopped, setPollStopped] = useState(false);
	// The first activated subscription_state seen. Kept, so a later profile that isn't
	// activated yet (a slow poll sent before the webhook landed) can't undo it.
	const [keptState, setKeptState] = useState(null);
	// Per user, so a later user in the same tab never gets this user's result
	const uuid = currentUser?.uuid;
	const { data: polledUser } = useQuery({
		queryKey: ['checkout-activation', uuid],
		queryFn: async () => {
			const user = await getUserData();
			// getUserData returns null without a token; don't wipe the user context then.
			// A stale poll can resolve after ['user-me'] has seen the activation itself
			// (e.g. on window focus); never downgrade ['user-me'] then.
			if (user) {
				queryClient.setQueryData(['user-me'], current =>
					isSubscriptionActivated(current?.subscription_state) &&
					!isSubscriptionActivated(user.subscription_state)
						? current
						: user
				);
			}
			return user;
		},
		enabled: flow === 'activation' && !pollStopped && !keptState,
		retry: false, // HTTPClient handles retries internally
		gcTime: 0, // dropped from the cache as soon as the page unmounts
		// TanStack v5 passes the query; stop once the Stripe subscription has landed
		refetchInterval: query =>
			isSubscriptionActivated(query.state.data?.subscription_state)
				? false
				: timedOut
					? SLOW_POLL_INTERVAL_MS
					: POLL_INTERVAL_MS,
	});

	useEffect(() => {
		if (flow !== 'activation') return;
		const slowTimer = setTimeout(() => setTimedOut(true), ACTIVATION_TIMEOUT_MS);
		const stopTimer = setTimeout(() => setPollStopped(true), POLL_STOP_MS);
		return () => {
			clearTimeout(slowTimer);
			clearTimeout(stopTimer);
		};
	}, [flow]);

	// Successful polls feed ['user-me'], but ['user-me'] also refreshes on its own (window
	// focus), including after polling has stopped. Activate on whichever sees it first.
	const newState =
		flow === 'activation' && !keptState
			? ([polledUser?.subscription_state, currentUser?.subscription_state].find(
					isSubscriptionActivated
				) ?? null)
			: null;
	const activatedState = keptState ?? newState;
	const activated = !!activatedState;

	// Once activated: keep the state, stop polling (`enabled` above) and drop the poll's
	// result from the cache
	useEffect(() => {
		if (!newState) return;
		setKeptState(newState);
		queryClient.removeQueries({ queryKey: ['checkout-activation', uuid], exact: true });
	}, [newState, queryClient, uuid]);

	// Redirect a few seconds after every finished flow
	const shouldRedirect = flow === 'canceled' || flow === 'payment_done' || activated;
	useEffect(() => {
		if (!shouldRedirect) return;
		const timer = setTimeout(() => navigate(redirectTo, { replace: true }), REDIRECT_DELAY_MS);
		return () => clearTimeout(timer);
	}, [shouldRedirect, redirectTo, navigate]);

	const renderContent = () => {
		switch (flow) {
			case 'start':
				if (!startFailed) return <Working text="Taking you to secure checkout…" />;
				if (startError?.code === 'account_not_verified') {
					return <Alert variant="danger">{VERIFY_EMAIL_COPY}</Alert>;
				}
				return (
					<Alert variant="danger">
						We couldn't start checkout. Please try again, or contact{' '}
						<a href="mailto:post@biodrone.no">post@biodrone.no</a> if it keeps
						happening.
					</Alert>
				);

			case 'activation':
				if (activated) {
					return (
						<Alert variant="success">
							{activatedState.plan_name
								? `Your ${activatedState.plan_name} subscription is active.`
								: 'Your subscription is active.'}{' '}
							Taking you back…
						</Alert>
					);
				}
				// `pending` lands here too: success=true means Stripe took the payment
				if (timedOut) {
					return (
						<Alert variant="info">
							Payment received, but your subscription is taking longer than usual to
							activate. It should appear shortly. If it doesn't, contact{' '}
							<a href="mailto:post@biodrone.no">post@biodrone.no</a>
							{referenceId ? (
								<>
									{' '}
									and include reference <code>{referenceId}</code>.
								</>
							) : (
								'.'
							)}
						</Alert>
					);
				}
				return <Working text="Payment received. Activating your subscription…" />;

			case 'payment_done':
				return <Alert variant="success">Payment received. Taking you back…</Alert>;

			case 'canceled':
				return (
					<Alert variant="info">
						Checkout canceled. You haven't been charged. Taking you back…
					</Alert>
				);

			default:
				return (
					<Alert variant="info">
						No plan selected.{' '}
						<Link to="/subscription">
							<strong>Choose a plan</strong>
						</Link>
					</Alert>
				);
		}
	};

	return (
		<View title="Checkout" maxWidth="800">
			{renderContent()}
		</View>
	);
}
